/**
 * @fileoverview RecPermissionQuestion component for rendering a recording permission question
 *
 * @example
 * // Usage in component:
 * import { RecPermissionQuestion } from './RecPermission'
 *
 * <RecPermissionQuestion onSubmit={onSubmit} question={question} />
 */

import { MediaDevice } from '../../MediaDevice'
import { NavigationButton } from '../NavigationButton'
import { Instructions } from './sections/Instructions'

import { useTranslation } from 'react-i18next'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { Text } from '../typography/Typography'
import css from './Checkbox.module.css'
import { Title } from './sections/Title'

/**
 * @function RecPermissionQuestion
 * @description Component for rendering a recording permission question
 * @param {Novoic.CheckboxQuestionProps} props - The props for the component
 * @returns {JSX.Element} The RecPermissionQuestion component
 */
export function RecPermissionQuestion({ onSubmit, question }: Novoic.CheckboxQuestionProps) {
    const { id, question: body } = question
    const { title, instructions, subtext } = body as Novoic.SoundCheckQuestionBody

    const { t } = useTranslation()

    async function onClick() {
        const mediaDevice = new MediaDevice()
        if (!mediaDevice.isSupportingAudioRecording()) {
            return
        }

        try {
            const stream = await mediaDevice.getStream()

            onSubmit(id, {
                checkboxes: {
                    permission: true,
                },
            })

            stream.getTracks().forEach((track) => {
                if (track.readyState == 'live') {
                    track.stop()
                }
            })
        } catch (e) {
            onSubmit(id, {
                checkboxes: {
                    permission: false,
                },
            })
        }
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title>{title}</Title>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}

            <div className={css.button_container}>
                <DesktopProgress />
                <NavigationButton type="confirm" label={t('buttons.agree')} onClick={onClick} />
            </div>
        </div>
    )
}
